import Component from '@glimmer/component'
import { capitalize } from '@ember/string'
import { formatNumber } from 'district-ui-client/utils/giraffe/formatter'
import { roundAccurately } from 'district-ui-client/utils/round'
import { PieChart } from 'district-ui-client/components/primitives/pie-chart/component'
import type { Point, SeriesPieOptions } from 'highcharts'
import { colors } from '@colors'
import { reportingToolTip as toolTipConfig } from 'district-ui-client/utils/giraffe/highcharts-config'
import type { BooksReadByGenreData } from 'district-ui-client/utils/student-events/fetchers/books-read-by-genre'

/**
 * Display a donut chart of books read by genre (fiction/nonfiction).
 */

interface Signature {
  Args: {
    data: BooksReadByGenreData
    product: LegacyProductRe | LegacyProductRex
  }
}

export class BooksReadByGenreDashboardChart extends Component<Signature> {
  get chartData(): SeriesPieOptions[] {
    const data = this.booksReadData

    return [
      {
        data,
        dataLabels: { enabled: false },
        showInLegend: true,
        type: 'pie',
      },
    ]
  }

  get chartInnerSize() {
    return '80%'
  }

  get chartSubtitle() {
    const subtitle = formatNumber(this.totalCount, 1)
    return `<h1>${subtitle}</h1>`
  }

  get chartSize() {
    return '80%'
  }

  get booksReadData(): [string, number][] {
    const values = this.args.data
    const keys = ['nonfiction', 'fiction'] as const
    return keys.map((key) => {
      return [capitalize(key), values[key]]
    })
  }

  get totalCount() {
    const values = this.args.data
    const keys = ['nonfiction', 'fiction'] as const
    return keys.reduce((total, key) => {
      return total + values[key]
    }, 0)
  }

  get colours() {
    return [colors.purplyPink300, colors.purplyPink400]
  }

  get legendData() {
    const total = this.totalCount
    const data = this.booksReadData

    const [[nonfictionName, nonfiction], [fictionName, fiction]] = data

    let fictionPercentage = 0
    let nonfictionPercentage = 0

    if (total !== 0) {
      fictionPercentage = (fiction / total) * 100
      nonfictionPercentage = (nonfiction / total) * 100
    }

    return {
      left: { name: fictionName, percentage: roundAccurately(fictionPercentage, 1) },
      right: { name: nonfictionName, percentage: roundAccurately(nonfictionPercentage, 1) },
    }
  }

  get tooltipFormatter() {
    return function (this: Point) {
      return `<b>${this.name}</b>: ${formatNumber(this.y ?? 0, 2)}`
    }
  }

  get tooltip() {
    const formatter = this.tooltipFormatter
    return toolTipConfig('y', '', { formatter })
  }

  <template>
    <div class="relative flex h-full flex-col pb-7 text-3xl font-light print:!font-light" data-test-books-read-by-genre>
      <PieChart
        @data={{this.chartData}}
        @subtitle={{this.chartSubtitle}}
        @colours={{this.colours}}
        @size={{this.chartSize}}
        @innerSize={{this.chartInnerSize}}
        @tooltip={{this.tooltip}}
      />
      <div data-test-books-read-by-genre-legend class="absolute bottom-2 left-0 right-0 flex">
        <div data-test-books-read-by-genre-label="left" class="ml-[5%] flex flex-col items-center">
          <div
            class="h-[10px] w-[10px] rounded-full bg-purply-pink-500 print:!rounded-full print:!bg-purply-pink-500"
          ></div>
          <div
            data-test-books-read-by-genre-percentage
            class="text-3xl font-thin text-dusty-black-500 print:!font-thin"
          >
            {{this.legendData.left.percentage}}%
          </div>
          <div data-test-books-read-by-genre-name class="m-0 text-base font-bold text-dusty-black-500 print:!font-bold">
            {{this.legendData.left.name}}
          </div>
        </div>

        <div data-test-books-read-by-genre-label="right" class="ml-auto mr-[5%] flex flex-col items-center">
          <div
            class="h-[10px] w-[10px] rounded-full bg-purply-pink-300 print:!rounded-full print:!bg-purply-pink-300"
          ></div>
          <div
            data-test-books-read-by-genre-percentage
            class="text-3xl font-thin text-dusty-black-500 print:!font-thin"
          >
            {{this.legendData.right.percentage}}%
          </div>
          <div data-test-books-read-by-genre-name class="m-0 text-base font-bold text-dusty-black-500 print:!font-bold">
            {{this.legendData.right.name}}
          </div>
        </div>
      </div>
    </div>
  </template>
}

export default BooksReadByGenreDashboardChart
